'use client';

import { useId } from 'react';
import type { ReactNode } from 'react';
import type { IconType } from 'react-icons';
import { FiMinus, FiPlus } from 'react-icons/fi';

import { ActionIcon, Box, Flex, Group, NumberInput, Text } from '@highlight-cp/core-ui';
import type { NumberInputProps } from '@highlight-cp/core-ui';

import { toSteppedValue } from './utils';

export type NumberStepperProps = Omit<
  NumberInputProps,
  | 'allowDecimal'
  | 'allowNegative'
  | 'debounceMs'
  | 'debounced'
  | 'descriptionProps'
  | 'hideControls'
  | 'inputContainer'
  | 'label'
  | 'labelProps'
  | 'max'
  | 'min'
  | 'onChange'
  | 'readOnly'
  | 'step'
  | 'value'
> & {
  label: ReactNode;
  /** `''` while the field is cleared. */
  value: number | '';
  onChange: (value: number | '') => void;
  min: number;
  max: number;
  /** Names the − button for what it changes, e.g. "Fewer days of use". */
  decrementLabel: string;
  /** Names the + button for what it changes, e.g. "More days of use". */
  incrementLabel: string;
  /** `left` puts the label on the stepper's line; the description stays below either way. */
  labelPosition?: 'top' | 'left';
  /** The number box's width. Omitted, it fills the row under a top label and is 75px beside one. */
  fieldWidth?: NumberInputProps['w'];
};

/** Figma's field beside a left label. */
const INLINE_FIELD_WIDTH = 75;

interface StepButtonProps {
  label: string;
  icon: IconType;
  /** The value a press steps to; `null` at the limit. */
  to: number | null;
  disabled?: boolean;
  onStep: (value: number) => void;
}

/** At its limit it only reads as disabled, so it keeps focus and pressing it does nothing. */
const StepButton = ({ label, icon: Icon, to, disabled, onStep }: StepButtonProps) => {
  const isAtLimit = to === null;

  return (
    <ActionIcon
      aria-disabled={isAtLimit}
      aria-label={label}
      color="var(--mantine-color-dimmed)"
      data-disabled={isAtLimit || undefined}
      disabled={disabled}
      onClick={() => !isAtLimit && onStep(to)}
      size="input-sm"
    >
      <Icon aria-hidden size={20} />
    </ActionIcon>
  );
};

/**
 * ### NumberStepper
 *
 * @description
 * A whole-number field between − and + buttons that step it by one within `min` and `max`. Typing
 * still works, clamped per `clampBehavior`. Clearing the field reports `''`; + then starts it at
 * `min`. The description is announced as it changes, so a live total can sit there.
 *
 * @example
 * ```tsx
 * <NumberStepper
 *   decrementLabel={t('Fewer days per product')}
 *   incrementLabel={t('More days per product')}
 *   label={t('Days per product')}
 *   labelPosition="left"
 *   max={30}
 *   min={1}
 *   onChange={setDays}
 *   value={days}
 * />
 * ```
 *
 * @param props - {@link NumberStepperProps}
 * @returns React component
 */
export default function NumberStepper({
  label,
  value,
  onChange,
  min,
  max,
  decrementLabel,
  incrementLabel,
  labelPosition = 'top',
  fieldWidth,
  disabled,
  id,
  ...props
}: NumberStepperProps) {
  const fallbackId = useId();
  const inputId = id ?? fallbackId;
  const isInline = labelPosition === 'left';
  const width = fieldWidth ?? (isInline ? INLINE_FIELD_WIDTH : undefined);

  // Top-aligned: the input carries a bottom margin when a description follows, which would pull
  // centred buttons below the field's edges.
  const stepper = (input: ReactNode) => (
    <Group align="flex-start" gap={4} wrap="nowrap">
      <StepButton
        disabled={disabled}
        icon={FiMinus}
        label={decrementLabel}
        onStep={onChange}
        to={toSteppedValue(value, -1, min, max)}
      />
      <Box
        __vars={{ '--input-text-align': 'center' }}
        flex={width === undefined ? 1 : undefined}
        miw={0}
        w={width}
      >
        {input}
      </Box>
      <StepButton
        disabled={disabled}
        icon={FiPlus}
        label={incrementLabel}
        onStep={onChange}
        to={toSteppedValue(value, 1, min, max)}
      />
    </Group>
  );

  return (
    <NumberInput
      allowDecimal={false}
      allowNegative={false}
      {...props}
      descriptionProps={{ 'aria-live': 'polite' }}
      disabled={disabled}
      hideControls
      id={inputId}
      inputContainer={(input) =>
        isInline ? (
          <Group align="flex-start" gap="sm" justify="space-between" wrap="nowrap">
            <Flex align="center" h="var(--hl-field-height-sm)">
              <Text component="label" fw={600} htmlFor={inputId} size="xs">
                {label}
              </Text>
            </Flex>
            {stepper(input)}
          </Group>
        ) : (
          stepper(input)
        )
      }
      label={isInline ? undefined : label}
      max={max}
      min={min}
      onChange={onChange}
      value={value}
    />
  );
}
